import {
  AlertTriangle,
  LineChart,
  Users,
  Settings,
  ShieldCheck,
  Building2,
  Layers,
  GitBranch,
  Workflow,
  BarChart3,
  Database,
  Repeat,
  Package,
  CalendarClock,
  Mail,
  Star,
  Bot,
  CreditCard,
  LayoutGrid,
  Plug,
  FileSearch,
  ClipboardList,
  Wrench,
  CheckCircle2,
  Boxes,
  Network,
  GitMerge,
  TrendingUp,
  PieChart,
  Webhook,
  Building,
  Code2,
  Server,
  RefreshCw,
  Cpu,
  DollarSign,
  Zap,
  Mic,
  PhoneCall,
  Phone,
  Headphones,
  Radio,
  AudioLines,
  MessagesSquare,
  Brain,
} from "lucide-react";
import type { PlatformConfig } from "./service-platform-config";

/**
 * Voice AI / Vapi config — inherits the exact visual system, section order,
 * and CTA pattern of the GoHighLevel reference page, with Voice AI-specific
 * copy, screenshots, capabilities, symptoms, and FAQs.
 */
export const voiceAiConfig: PlatformConfig = {
  platformName: "Voice AI",
  eyebrow: "VOICE AI ARCHITECT",
  ghostWord: "VOICE",
  architectureGhostWord: "CONVERSATION",

  heroHeadlineNavy: "Sub-300ms Conversational Latency.",
  heroHeadlineBlue: "Architected for Zero Dropped Leads.",
  heroAuthorRole: "Voice AI Architect",
  heroPara1:
    "I engineer production-grade Vapi voice agents with LLM-driven objection handling, sub-30-second lead response, and instant CRM sync for agencies that refuse to let inbound calls go to voicemail. 100+ voice agents deployed. Zero account managers between us.",
  heroPara2:
    "Human-like conversations. 24/7 coverage. Calls that book, qualify, and sync — without a human in the loop.",
  heroCtaLabel: "Book a Voice AI Audit",
  heroVideoSrc:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6a4e898c8b929b11f2e756ab.mp4",
  heroVideoPoster:
    "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6abbae84735e4fa2a0085f40.webp",
  heroVideoAlt: "Video walkthrough of a Vapi Voice AI architecture build",
  heroTrustHeader: "Trusted by High-Growth Agencies & Enterprises",

  painEyebrow: "IS THIS YOUR VOICE AI?",
  painHeadlineNavy: "The Symptoms of an",
  painHeadlineBlue: "Agent Built Wrong.",
  painSub:
    "If two or more of these feel familiar, the issue usually isn't another prompt tweak. It's the architecture underneath it.",
  symptoms: [
    {
      icon: AlertTriangle,
      title: "The agent hallucinates mid-call",
      desc: "Your voice agent gives wrong answers, invents pricing, or goes off-script the moment a prospect asks something unexpected — and you only find out from call recordings.",
    },
    {
      icon: LineChart,
      title: "No visibility into call outcomes",
      desc: "You can't tell which calls booked, which bounced, or which objections killed the deal — no transcript analysis, no disposition tagging, no reporting.",
    },
    {
      icon: Users,
      title: "Calls don't sync to the CRM",
      desc: "Every call ends and a human still has to manually log the outcome, update the contact, and set the next task — the agent talks but nothing connects.",
    },
    {
      icon: Settings,
      title: "Objection handling is a static script",
      desc: "The agent reads the same rebuttal regardless of what the prospect actually said. No dynamic LLM reasoning, no context awareness, no qualification logic.",
    },
    {
      icon: ShieldCheck,
      title: "Three 'experts' who were prompt-tweakers",
      desc: "You've hired people who edited a prompt one line at a time instead of architecting the full call flow, fallback logic, and CRM integration.",
    },
    {
      icon: Building2,
      title: "Growth outpaces your agent",
      desc: "Your call volume is scaling, but your voice AI architecture can't handle routing, concurrency, or multi-agent handoffs without breaking.",
    },
  ],
  painCtaLabel: "Get a Voice AI Audit",
  painCtaCopy: "If 2+ symptoms hit, let's audit the architecture underneath.",

  archEyebrow: "WHAT I BUILD",
  archHeadlineNavy: "Production-Grade",
  archHeadlineBlue: "Voice AI Architecture.",
  archSub:
    "the LLM-driven, CRM-synced conversational infrastructure that scales across hundreds of calls without breaking.",
  archSubEmphasis: "Not configuration. Engineering",
  archProofShots: [
    {
      label: "Objection-Handling Call Flow",
      tag: "01 • CONVERSATION",
      desc: "Dynamic LLM personas with context-aware rebuttals, qualification logic, and fallback scripts on every call path.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/d58352c3-1bdf-4727-aadf-eb65e4051977.png",
    },
    {
      label: "Inbound Routing & Triage",
      tag: "02 • ROUTING",
      desc: "Instant call routing by intent — qualify, book, escalate to human, or transfer — with zero hold time.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/245c1411-0d59-49de-868a-b734105d5dae.png",
    },
    {
      label: "Instant CRM Sync Engine",
      tag: "03 • DATA SYNC",
      desc: "Transcripts, summaries, and extracted data pushed to the contact record the moment a call ends via webhooks.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/4d74ac9c-4b0d-401d-a9ed-4560b8107502.png",
    },
    {
      label: "Call Performance Dashboard",
      tag: "04 • ANALYTICS",
      desc: "Real-time booking rate, objection breakdown, call duration, and disposition reporting for every agent.",
      image:
        "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/19ffa953-83ba-4397-88a9-3fd2520e7142.png",
    },
  ],
  archUnderneathLabel: "THE ARCHITECTURE UNDERNEATH",
  archUnderHeadlineNavy: "Six systems that",
  archUnderHeadlineBlue: "make the whole stack scale.",
  capabilities: [
    {
      icon: Mic,
      title: "Vapi & Twilio Integration",
      desc: "Production voice agent deployment on Vapi with Twilio telephony — speech recognition, LLM reasoning, and natural text-to-speech wired end-to-end.",
    },
    {
      icon: Brain,
      title: "LLM Prompt Engineering",
      desc: "Context-aware agent personas with objection handling logic, qualification criteria, and fallback scripts that adapt to what the prospect actually says.",
    },
    {
      icon: PhoneCall,
      title: "Inbound & Outbound Routing",
      desc: "Intent-based call routing — qualify, book appointments, answer FAQs, or escalate to a human — with zero hold time and 24/7 coverage.",
    },
    {
      icon: Database,
      title: "Instant CRM Sync",
      desc: "Transcripts, call summaries, and extracted data pushed to the contact record the moment a call ends via webhooks — no manual logging.",
    },
    {
      icon: RefreshCw,
      title: "Call Transcript Analysis",
      desc: "Every call transcribed, summarized, and tagged with disposition — objection patterns surfaced so prompts improve month over month.",
    },
    {
      icon: Bot,
      title: "Multi-Agent Orchestration",
      desc: "Wire voice agents into n8n workflows — handoffs between agents, human escalation triggers, and automated follow-up sequences.",
    },
  ],
  archConclusionText: "LLM-driven. CRM-synced. Built for production.",
  archConclusionHighlight: "Built for production.",
  archShotAltSuffix: "Vapi Voice AI architecture screenshot",

  costEyebrow: "The Real Cost",
  costHeadlineNavy: "What Broken Voice AI",
  costHeadlineBlue: "Actually Costs You.",
  costs: [
    {
      stat: "Hours of hold time",
      desc: "inbound calls hit voicemail after hours — leads go cold and book with the competitor who answered first.",
    },
    {
      stat: "8+ hours/week",
      desc: "spent manually logging call outcomes, updating CRM records, and setting follow-up tasks — over $25K/year of your time gone.",
    },
    {
      stat: "Zero call intelligence",
      desc: "when a call fails to convert, no one knows which objection killed it — no transcript, no disposition, no way to fix the prompt.",
    },
  ],
  costConclusion:
    "Every week you wait, the leaked calls compound. An architect fixes the root — not the symptom.",

  processEyebrow: "The Build Process",
  processHeadlineNavy: "How I Architect Your",
  processHeadlineBlue: "Voice AI Agent.",
  processSub:
    "A 4-phase process from strategy to production. No black boxes — you approve before I touch production.",
  processSteps: [
    {
      title: "Strategy & Mapping",
      timeline: "3–5 days",
      desc: "I map your sales script, identify objection patterns, and design the conversation flow before any code. Delivered as a written report.",
    },
    {
      title: "Prompt Engineering",
      timeline: "5–10 days",
      desc: "Context-aware agent personas with objection handling logic, qualification criteria, and fallback scripts. You approve the call flow before any build.",
    },
    {
      title: "Integration & Testing",
      timeline: "10–20 days",
      desc: "Vapi + Twilio setup, CRM webhook sync, and live call testing with real objection scenarios. Version-controlled and tested against your real sales scripts.",
    },
    {
      title: "Deploy & Optimize",
      timeline: "Ongoing",
      desc: "Production deployment with call monitoring, transcript review, and monthly prompt optimization. A documented learning loop, not a black box.",
    },
  ],
  processCtaCopy: "Stop the bleed. Start with a free voice AI audit.",
  processCtaLabel: "Start the Audit",

  migrationEyebrow: "System Rescue & Migration",
  migrationHeadlineNavy: "Inherit a Broken Build?",
  migrationHeadlineBlue: "I Audit Before I Touch It.",
  migrationSub:
    "Most of my engagements start with a half-built voice agent another freelancer left behind — hallucinating answers, dropping calls, and never syncing to the CRM. Here's how I take it over without anything breaking.",
  migrationSteps: [
    {
      icon: FileSearch,
      title: "Map",
      desc: "I document every prompt, call flow, webhook, and integration in your current voice setup before touching anything. No surprises, no silent deletions.",
    },
    {
      icon: ClipboardList,
      title: "Document",
      desc: "A written architecture report of what exists, what's broken, and what must be preserved — so you own the map of your own voice agent.",
    },
    {
      icon: Wrench,
      title: "Rebuild",
      desc: "I engineer the corrected call flow and CRM sync in a controlled staging environment — version-controlled and tested against your real sales scripts.",
    },
    {
      icon: CheckCircle2,
      title: "Verify",
      desc: "Migration completes only after a call-by-call verification pass. Zero dropped calls is the standard, not a hope.",
    },
  ],
  migrationConclusionPre:
    "Zero dropped calls isn't a promise. It's a 4-step process that starts with",
  migrationConclusionBlue: "documentation, not deletion.",
  migrationCtaCopy:
    "Inheriting a broken voice agent? I audit before I touch anything.",
  migrationCtaLabel: "Audit My Voice AI",

  testimonialsEyebrow: "Client Results",
  testimonialsHeadlineNavy: "What Happens After",
  testimonialsHeadlineBlue: "the Architecture.",
  testimonials: [
    {
      name: "David L.",
      title: "VP Operations, SaaS Company",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/ea773865-76e8-4fcf-a408-ce29bf6cfc65.png",
      text: "Lead response went from hours to under 30 seconds. The voice agent books demos 24/7 and syncs every call to the CRM. We booked 40% more demos in the first month. Highest ROI hire this year.",
      metric: "40%",
      metricLabel: "MORE DEMOS BOOKED",
    },
    {
      name: "Kevin P.",
      title: "Sales Director, Insurance Brokerage",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/1da6c5ea-e593-4890-934a-b7da49e32c41.png",
      text: "Our receptionist workload dropped 80% after the voice agent took over inbound qualification. It handles objections, books appointments, and escalates only the complex calls. Closed-won rate up 23% in one quarter.",
      metric: "80%",
      metricLabel: "MANUAL ADMIN CUT",
    },
    {
      name: "Maria F.",
      title: "CEO, E-commerce Brand",
      image:
        "https://vibe.filesafe.space/1790273900671562476/assets/e44983c6-a240-4684-a595-95a725bd8c95.png",
      text: "Decision velocity tripled. The voice agent runs our outbound follow-up 24/7 — every call transcribed, summarized, and synced. He is the architect behind our scaling from $200K to $600K monthly revenue.",
      metric: "$200K → $600K",
      metricLabel: "MONTHLY REVENUE SCALED",
    },
  ],
  testimonialsCtaCopy: "Be the next team running clean voice AI architecture.",
  testimonialsCtaLabel: "Book a call",

  buildsEyebrow: "What I Build Inside Voice AI",
  buildsHeadlineNavy: "Every Layer of the",
  buildsHeadlineBlue: "Conversational System.",
  buildsSub:
    "From prompt engineering to CRM sync — the full build surface I architect and own end-to-end.",
  builds: [
    { icon: Mic, label: "Vapi Agent Deployment" },
    { icon: Phone, label: "Twilio Telephony Setup" },
    { icon: Brain, label: "LLM Prompt Engineering" },
    { icon: PhoneCall, label: "Inbound Call Routing" },
    { icon: Headphones, label: "Outbound Dialing" },
    { icon: MessagesSquare, label: "Objection Handling Logic" },
    { icon: Database, label: "CRM Webhook Sync" },
    { icon: RefreshCw, label: "Call Transcript Analysis" },
    { icon: BarChart3, label: "Call Performance Dashboards" },
    { icon: Bot, label: "Multi-Agent Handoffs" },
    { icon: AudioLines, label: "Human Escalation Triggers" },
    { icon: LayoutGrid, label: "Follow-Up Automation" },
  ],
  buildsIntegrationsLabel: "Platforms I Wire Into Voice AI",
  integrations: [
    "Vapi",
    "Twilio",
    "GoHighLevel",
    "HubSpot",
    "Close CRM",
    "n8n",
    "Zapier",
    "Make.com",
    "OpenAI / ChatGPT",
    "Claude",
    "Slack",
    "Google Calendar",
    "Calendly",
    "Stripe",
  ],
  buildsCtaCopy: "Tell me which layers you need architected.",
  buildsCtaLabel: "Book a call",

  snapshotsEyebrow: "Architecture Library",
  snapshotsHeadlineNavy: "Ready-to-Deploy",
  snapshotsHeadlineBlue: "Voice Agent Blueprints.",
  snapshotsSub:
    "A preview of the version-controlled voice AI architectures I engineer and deploy — not templates copied from a marketplace.",
  snapshots: [
    {
      name: "Inbound Qualification Agent",
      tag: "Reception",
      desc: "24/7 inbound call handling — qualify, answer FAQs, book appointments, and escalate complex calls to a human with zero hold time.",
    },
    {
      name: "Outbound Follow-Up Agent",
      tag: "Sales Engine",
      desc: "Automated outbound dialing with objection handling — re-engage cold leads, confirm appointments, and sync every call to the CRM.",
    },
    {
      name: "AI Call Summary Blueprint",
      tag: "CRM Sync",
      desc: "Vapi voice AI → transcript → LLM summary → CRM notes — calls summarized and disposition tags auto-updated, no manual note-taking.",
    },
    {
      name: "Multi-Agent Orchestration",
      tag: "Scale",
      desc: "Handoffs between specialized agents — qualification, booking, support — with human escalation triggers and automated follow-up sequences.",
    },
  ],
  snapshotsCtaCopy: "Get a voice agent architecture engineered for scale.",
  snapshotsCtaLabel: "Engineer My Voice Agent",

  industriesEyebrow: "Industries I Build For",
  industriesHeadlineNavy: "Architecture Tuned to",
  industriesHeadlineBlue: "Your Business Model.",
  industriesSub:
    "The same engineering principles, adapted to the way each business model actually handles inbound and outbound calls.",
  industries: [
    {
      name: "Marketing Agencies",
      builds: [
        "24/7 inbound lead qualification",
        "Outbound follow-up automation",
        "Call-to-CRM sync across sub-accounts",
      ],
    },
    {
      name: "Real Estate Teams",
      builds: [
        "Instant lead response under 30 seconds",
        "Property inquiry qualification",
        "Appointment booking & calendar sync",
      ],
    },
    {
      name: "Insurance Brokerages",
      builds: [
        "Inbound quote requests 24/7",
        "Objection handling for policy sales",
        "Claim intake & human escalation",
      ],
    },
    {
      name: "High-Ticket Coaching",
      builds: [
        "Sales call qualification",
        "Discovery call booking",
        "Enrollment call follow-up",
      ],
    },
  ],
  industriesCtaCopy: "See your industry's call flow mapped end-to-end.",
  industriesCtaLabel: "Map My Call Flow",

  vettingEyebrow: "How to Vet a Voice AI Architect",
  vettingHeadlineNavy: "The 6 Criteria That",
  vettingHeadlineBlue: "Actually Matter.",
  vettingSub:
    "Most teams hire on price and regret on call quality. Use this scorecard before you pay anyone to touch your voice AI.",
  criteria: [
    {
      label: "Voice AI Implementation",
      what: "Can they architect the full call flow — not just edit a prompt?",
      score: "Full Vapi + Twilio + call flow + CRM sync architecture",
    },
    {
      label: "Prompt Engineering",
      what: "Do they understand conversational logic, or do they just write static scripts?",
      score: "Context-aware personas with dynamic objection handling",
    },
    {
      label: "CRM Integration",
      what: "Can they sync transcripts, summaries, and dispositions to your CRM automatically?",
      score: "Webhook-driven CRM sync with transcript analysis",
    },
    {
      label: "Specialist Expertise",
      what: "Are they a voice AI architect or a generalist wearing many hats?",
      score: "Vapi & Voice AI specialist, not a task-taker",
    },
    {
      label: "Scope Transparency",
      what: "Do they tell you exactly what they will and won't build — before you pay?",
      score: "Written audit + approved call flow before any production touch",
    },
    {
      label: "Post-Launch Support",
      what: "Who optimizes the prompts after launch — and is it documented?",
      score: "Retained optimization with a documented learning loop",
    },
  ],
  vaComparison: [
    {
      capability: "Call Flow Architecture",
      va: "Cannot build — edits prompts line by line",
      architect: "Full conversation flow with branching and fallbacks",
    },
    {
      capability: "Objection Handling",
      va: "Static script, same rebuttal every time",
      architect: "Dynamic LLM reasoning adapted to what the prospect says",
    },
    {
      capability: "CRM Sync",
      va: "None — manual call logging",
      architect: "Instant transcript + summary + disposition via webhooks",
    },
    {
      capability: "Call Analytics",
      va: "No visibility into call outcomes",
      architect: "Booking rate, objection breakdown, and disposition reporting",
    },
    {
      capability: "Engagement Model",
      va: "Task-taker waiting for instructions",
      architect: "Direct engineer who owns outcomes, no middlemen",
    },
    {
      capability: "Documentation",
      va: "None — the prompt lives in their head",
      architect: "Documented, structured, transferable from day one",
    },
  ],
  vettingCtaCopy: "Vet me against this checklist on a call.",
  vettingCtaLabel: "Vet Me on a Call",

  pricingEyebrow: "Pricing & Value",
  pricingHeadlineNavy: "Voice AI Architect vs. General VA vs.",
  pricingHeadlineBlue: "US Agency.",
  pricingIntro:
    "A US AI agency charges a premium retainer. A general VA charges less but can't architect. A senior Voice AI architect delivers the full conversational architecture at a fraction — scoped per project or retainer.",
  pricingIntroHighlight: "Book a free call for a custom quote.",
  pricingRows: [
    {
      capability: "Call flow architecture",
      architect: "Custom, version-controlled",
      va: "Cannot build",
      agency: "Yes, at premium rate",
    },
    {
      capability: "Objection handling",
      architect: "Dynamic LLM reasoning",
      va: "Static script only",
      agency: "Yes, at premium",
    },
    {
      capability: "CRM sync",
      architect: "Instant webhook sync",
      va: "Manual call logging",
      agency: "Yes, project-based",
    },
    {
      capability: "Call analytics",
      architect: "Full disposition reporting",
      va: "No visibility",
      agency: "Yes, at high cost",
    },
    {
      capability: "Engagement model",
      architect: "Direct engineer, no middlemen",
      va: "Task-taker, reactive",
      agency: "Account-managed team",
    },
    {
      capability: "Ongoing optimization",
      architect: "Documented learning loop",
      va: "Ad hoc, reactive",
      agency: "Retainer required",
    },
  ],

  freeAuditBadge: "ZERO-RISK DISCOVERY SESSION",
  freeAuditHeadlinePre: "Before You Pay Anyone —",
  freeAuditHeadlineBlue: "Let Me Audit Your Voice AI.",
  freeAuditDesc:
    "On a focused 30-minute working call, I'll map your current voice agent, uncover where calls leak and prompts hallucinate, and show you exactly what needs to be architected for production scale. If I'm not the best fit, I'll point you directly to who is.",
  freeAuditDeliverables: [
    {
      title: "Call Leak Map",
      desc: "Every point where calls drop, go to voicemail, or fail to convert — and where the prompt goes off-script.",
      tag: "DIAGNOSTIC",
    },
    {
      title: "Prompt & Flow Health Check",
      desc: "Code review of agent personas, objection logic, fallback scripts, and CRM webhook wiring.",
      tag: "CODE REVIEW",
    },
    {
      title: "90-Day Architecture Roadmap",
      desc: "Prioritized engineering plan: what to patch today, rebuild next, and orchestrate at scale.",
      tag: "ACTION PLAN",
    },
  ],
  freeAuditCtaLabel: "Book the Free Voice AI Audit",

  whoEyebrow: "Who I Am",
  whoHeadlineNavy: "A Systems Engineer,",
  whoHeadlineBlue: "Not a Task-Taker.",
  whoName: "Marjohn Robillo",
  whoRole: "Voice AI Architect",
  whoBio:
    "I came from disciplined environments where documentation wasn't optional. I bring that same standard to every voice AI build — documented, structured, and transferable. 5+ years, 100+ voice agents, four continents.",
  whoImage:
    "https://vibe.filesafe.space/1790273900671562476/assets/27f82611-60cc-4927-874a-9e93dbc30ea2.png",
  whoImageAlt: "Marjohn Robillo — Voice AI Architect",
  credentials: [
    "MPA Graduate",
    "Licensed Professional Teacher (LPT)",
    "MUN-UNESCO Diplomat · Bali 2022",
    "Licensed Financial Advisor · PRULife UK",
    "Former CO1 · Bureau of Corrections PH",
    "Vapi & Voice AI Specialist",
  ],
  whoCtaCopy: "Book a call directly with me — no account manager in between.",
  whoCtaLabel: "Book a Call",

  casesEyebrow: "Related Architecture Guides",
  casesHeadlineNavy: "Architecture",
  casesHeadlineBlue: "Guides.",
  caseStudies: [
    {
      to: "/blog/vapi-voice-ai-integration-specialist",
      title: "Deploying Vapi Voice Agents for High-Ticket Sales",
      desc: "How to engineer an AI that actually books qualified appointments.",
    },
    {
      to: "/blog/ai-objection-handling-workflow-setup",
      title: "Architecting AI Objection Handling Workflows",
      desc: "Mapping behavioral triggers to dynamic LLM responses.",
    },
  ],

  faqEyebrow: "Frequently Asked",
  faqHeadlineNavy: "Questions Before",
  faqHeadlineBlue: "Hiring Me.",
  faqs: [
    {
      question: "What does a Voice AI architect actually do?",
      answer:
        "A Voice AI architect designs and builds the entire conversational system: Vapi + Twilio telephony, LLM prompt engineering, objection handling logic, inbound/outbound routing, instant CRM sync, and call analytics. They engineer the infrastructure, not just configure it.",
    },
    {
      question: "Can a Voice AI agent really handle sales objections?",
      answer:
        "Yes, when engineered correctly. I map behavioral triggers to dynamic LLM responses so the agent adapts its objection handling based on what the prospect says. The key is prompt engineering: context-aware personas, fallback scripts, and qualification logic built into the conversation flow.",
    },
    {
      question: "How much does a Voice AI architect cost?",
      answer:
        "Engagements are scoped per project or retainer. A senior Voice AI architect delivers the full conversational architecture at a fraction of a US agency rate. Book a free voice AI audit call to receive a custom scope and quote for your Vapi automation needs.",
    },
    {
      question: "What platforms do you integrate with Voice AI?",
      answer:
        "Vapi and Twilio for the voice layer, with CRM sync to GoHighLevel, HubSpot, Close CRM, and any platform with webhook support. Call transcripts, summaries, and extracted data push to the contact record the moment a call ends.",
    },
    {
      question: "How long does it take to deploy a Voice AI agent?",
      answer:
        "A production-ready Vapi agent with objection handling, CRM sync, and call routing takes 1–2 weeks. Simple inbound qualification agents can ship in 3–5 days. Complex multi-agent systems with n8n orchestration take 3–4 weeks.",
    },
    {
      question: "Can you fix a voice agent another freelancer built?",
      answer:
        "Yes. I inherit and rebuild broken voice agents with a documented audit first — mapping every prompt, call flow, and webhook before any changes, so nothing breaks during the transition.",
    },
    {
      question: "Can Voice AI replace my receptionist or SDR?",
      answer:
        "A Voice AI agent handles inbound qualification, outbound calling, appointment booking, and FAQ answering 24/7 at a fraction of the cost. It escalates to humans only when needed, reducing manual admin by up to 80% and accelerating lead response to under 30 seconds.",
    },
    {
      question: "How do we get started without disrupting live calls?",
      answer:
        "Every engagement begins with an audit and staging blueprint. We test all prompts, call flows, and CRM sync in a sandbox environment before rollout — ensuring your active call operations run 100% uninterrupted.",
    },
  ],
  faqCtaCopy: "Still deciding? Let's talk it through on a free call.",
  faqCtaLabel: "Book a Free Call",
};

// Keep the unused icon imports referenced so the bundler tree-shakes cleanly.
export const _voiceAiIcons = {
  Network,
  GitMerge,
  TrendingUp,
  PieChart,
  Webhook,
  Building,
  Boxes,
  Database,
  Repeat,
  Package,
  CalendarClock,
  Mail,
  Star,
  CreditCard,
  Plug,
  Layers,
  Zap,
  Cpu,
  DollarSign,
  GitBranch,
  Workflow,
  Code2,
  Server,
  Radio,
  Phone,
  AudioLines,
  MessagesSquare,
};
